import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatSidenav } from '@angular/material/sidenav';
import { MatStepper } from '@angular/material/stepper';
import { SidebarNavMenuItem } from '../../../decorators/sidebar-nav-menu-item';
import { BusinessProcessEntity } from '../../../models/settings/business-process-entity';
import { BusinessProcessEntityService } from '../../../services/settings/businessprocess-entity.service';
import { Log, IShortLogger } from '../../../services/logger.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../shared/pagination-options';

@SidebarNavMenuItem({
  route: '/manage-rights',
  faIcon: 'security',
  name: 'Manage Rights',
  order: 2200,
  businessProcessesName: 'BusinessProcessRights'
})
@Component({
  selector: 'app-business-process',
  templateUrl: './business-process.component.html',
  styleUrls: ['./business-process.component.css']
})
export class BusinessProcessComponent implements OnInit, AfterViewInit {

  private readonly logger: IShortLogger<BusinessProcessComponent> = Log.create(BusinessProcessComponent);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('sidenav') sidenav!: MatSidenav;
  @ViewChild('rightsStepper') rightsStepper!: MatStepper;

  dataSource = new MatTableDataSource<BusinessProcessEntity>([]);
  readonly displayedColumns = ['displayName', 'description', 'isActive', 'rights'];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  searchTerm = '';
  selectedBusinessProcess: BusinessProcessEntity | null = null;
  selectedActivity: any = null;
  selectedAction: any = null;
  pendingTeam: any = null;
  pendingOperation: 'assign' | 'revoke' | null = null;
  private closeOnSuccess = false;
  drawerStep = 0;
  availableTeams: any[] = [];
  loadingTeams: Map<string, boolean> = new Map();
  public isLoading = false;
  loading = false;
  error: string | null = null;

  /** Non-system teams only — these are the ones whose rights can be managed. */
  get assignableTeams(): any[] {
    return this.availableTeams.filter(t => t.isDeletable === true);
  }

  /** True when all teams in the system are protected and none can have rights configured. */
  get noAssignableTeams(): boolean {
    return this.availableTeams.length > 0 && this.assignableTeams.length === 0;
  }

  constructor(
    private businessProcessService: BusinessProcessEntityService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadBusinessProcesses();
    this.loadTeams();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  notify(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info'): void {
    const panelClass: Record<string, string> = {
      success: 'snack-success',
      error: 'snack-error',
      warning: 'snack-warning',
      info: 'snack-info'
    };
    this.snackBar.open(message, 'Close', { duration: 3000, panelClass: panelClass[type] });
  }

  onSearchChange(): void {
    this.dataSource.filter = this.searchTerm.trim().toLowerCase();
  }

  loadBusinessProcesses(): void {
    this.loading = true;
    this.error = null;

    this.businessProcessService.getAllBusinessProcess().subscribe({
      next: (result) => {
        this.dataSource.data = result;

        // Refresh references after reload
        if (this.selectedBusinessProcess) {
          const updated = this.dataSource.data.find(bp => bp.id === this.selectedBusinessProcess?.id);
          if (updated) {
            this.selectedBusinessProcess = updated;

            if (this.selectedActivity) {
              const updatedActivity = updated.activities?.find(
                (act: any) => act.name === this.selectedActivity.name
              );
              if (updatedActivity) {
                this.selectedActivity = updatedActivity;
                if (this.selectedAction) {
                  const updatedAction = updatedActivity.actions?.find(
                    (a: any) => a.name === this.selectedAction.name
                  );
                  if (updatedAction) this.selectedAction = updatedAction;
                }
              }
            }
          }
        }

        this.loading = false;
        this.logger.i('Loaded Business processes:', this.dataSource.data.length);
      },
      error: (err) => {
        this.loading = false;
        this.logger.e('Error loading Business Process:', err);
        this.error = 'Failed to load business processes';
      }
    });
  }

  loadTeams(): void {
    this.businessProcessService.getAllTeams().subscribe({
      next: (result) => {
        this.availableTeams = result;
        this.logger.i('Loaded Teams:', this.availableTeams.length);
      },
      error: (err) => {
        this.logger.e('Error loading Teams:', err);
      }
    });
  }

  async openSetRightsDrawer(bp: BusinessProcessEntity): Promise<void> {
    // If same row, toggle close
    if (this.sidenav.opened && this.selectedBusinessProcess?.id === bp.id) {
      await this.sidenav.close();
      return;
    }
    // Close first if switching rows
    if (this.sidenav.opened) {
      await this.sidenav.close();
    }
    this.selectedBusinessProcess = bp;
    this.selectedActivity = null;
    this.selectedAction = null;
    this.drawerStep = 0;
    await this.sidenav.open();
    // Reset stepper after drawer opens so it starts at step 0
    setTimeout(() => this.rightsStepper?.reset(), 0);
  }

  selectActivity(act: any): void {
    this.selectedActivity = act;
    this.selectedAction = null;
    this.drawerStep = 1;
    setTimeout(() => this.rightsStepper?.next(), 0);
  }

  selectAction(action: any): void {
    this.selectedAction = action;
    this.drawerStep = 2;
    setTimeout(() => this.rightsStepper?.next(), 0);
  }

  backToActivities(): void {
    this.selectedActivity = null;
    this.selectedAction = null;
    this.drawerStep = 0;
    setTimeout(() => this.rightsStepper?.reset(), 0);
  }

  backToActions(): void {
    this.selectedAction = null;
    this.drawerStep = 1;
    setTimeout(() => { this.rightsStepper?.reset(); this.rightsStepper?.next(); }, 0);
  }

  // Keep legacy method aliases used by toggleRight guard
  onActivitySelect(act: any): void { this.selectActivity(act); }
  onActionSelect(action: any): void { this.selectAction(action); }

  toggleRight(activity: any, action: any, team: any): void {
    if (!activity || !action) {
      this.notify('Please select an Activity and an Action first.', 'warning');
      return;
    }

    if (!action.rights) action.rights = {};
    if (!Array.isArray(action.rights.teams)) action.rights.teams = [];

    const key = this.getTeamKey(team);
    const idx = action.rights.teams.findIndex((t: any) =>
      (t.key ?? t.teamId ?? t.id ?? t?._id ?? t.team ?? t.name) === key
    );

    this.pendingTeam = team;
    this.pendingOperation = idx > -1 ? 'revoke' : 'assign';
    this.drawerStep = 3;
    setTimeout(() => this.rightsStepper?.next(), 0);
  }

  confirmRightsChange(): void {
    if (!this.pendingTeam || !this.pendingOperation || !this.selectedAction || !this.selectedActivity) return;

    const key = this.getTeamKey(this.pendingTeam);
    const payload = {
      Id: this.selectedBusinessProcess?.id,
      Activity: this.selectedActivity.name,
      action: this.selectedAction.name,
      team: {
        teamId: this.pendingTeam.teamId ?? this.pendingTeam.id,
        team: this.pendingTeam.name
      }
    };

    this.logger.i('Confirmed rights change:', this.pendingOperation, payload);
    this.loadingTeams.set(key, true);
    this.closeOnSuccess = true;

    const operation = this.pendingOperation;
    this.pendingTeam = null;
    this.pendingOperation = null;
    this.drawerStep = 2;
    setTimeout(() => {
      this.rightsStepper?.reset();
      this.rightsStepper?.next();
      this.rightsStepper?.next();
    }, 0);

    if (operation === 'revoke') {
      this.revokeRights(payload, key);
    } else {
      this.assignRights(payload, key);
    }
  }

  cancelRightsChange(): void {
    this.pendingTeam = null;
    this.pendingOperation = null;
    this.drawerStep = 2;
    setTimeout(() => {
      this.rightsStepper?.reset();
      this.rightsStepper?.next();
      this.rightsStepper?.next();
    }, 0);
  }

  onDrawerClosed(): void {
    this.selectedBusinessProcess = null;
    this.selectedActivity = null;
    this.selectedAction = null;
    this.pendingTeam = null;
    this.pendingOperation = null;
    this.drawerStep = 0;
  }

  hasRight(action: any, team: any): boolean {
    if (!action) return false;
    const teams = action?.rights?.teams ?? [];
    const key = this.getTeamKey(team);
    return teams.some((t: any) =>
      (t.key ?? t.teamId ?? t.id ?? t?._id ?? t.team ?? t.name) === key
    );
  }

  isTeamLoading(team: any): boolean {
    return this.loadingTeams.get(this.getTeamKey(team)) ?? false;
  }

  private getTeamKey(team: any): string {
    return team?.teamId ?? team?.id ?? team?._id ?? team?._id?.$oid ?? team?.name;
  }

  private getTeamName(team: any): string {
    return team?.name ?? team?.label ?? team?.teamName ?? String(this.getTeamKey(team));
  }

  assignRights(businessProcessData: any, teamKey?: string): void {
    this.logger.i('Assigning rights:', businessProcessData);
    if (!this.selectedBusinessProcess) return;

    this.loading = true;
    this.businessProcessService.assignRights(businessProcessData).subscribe({
      next: (response) => {
        if (teamKey) this.loadingTeams.delete(teamKey);
        if (response.success) {
          this.loadBusinessProcesses();
          this.notify(response.message, 'success');
          if (this.closeOnSuccess) { this.closeOnSuccess = false; this.sidenav.close(); }
        } else {
          this.closeOnSuccess = false;
          this.notify(response.message, 'warning');
          if (response.reason) this.notify(response.reason, 'warning');
          this.loadBusinessProcesses();
        }
        this.loading = false;
      },
      error: (error) => {
        this.closeOnSuccess = false;
        if (teamKey) this.loadingTeams.delete(teamKey);
        this.loading = false;
        this.logger.e('Error assigning rights:', error);
        this.notify('Failed to assign rights', 'error');
        this.loadBusinessProcesses();
      }
    });
  }

  revokeRights(businessProcessData: any, teamKey?: string): void {
    this.logger.i('Revoking rights:', businessProcessData);
    if (!this.selectedBusinessProcess) return;

    this.loading = true;
    this.businessProcessService.revokeRights(businessProcessData).subscribe({
      next: (response) => {
        if (teamKey) this.loadingTeams.delete(teamKey);
        if (response.success) {
          this.loadBusinessProcesses();
          this.notify(response.message, 'success');
          if (this.closeOnSuccess) { this.closeOnSuccess = false; this.sidenav.close(); }
        } else {
          this.closeOnSuccess = false;
          this.notify(response.message, 'warning');
          if (response.reason) this.notify(response.reason, 'warning');
          this.loadBusinessProcesses();
        }
        this.loading = false;
      },
      error: (error) => {
        this.closeOnSuccess = false;
        if (teamKey) this.loadingTeams.delete(teamKey);
        this.loading = false;
        this.logger.e('Error revoking rights:', error);
        this.notify('Failed to revoke rights', 'error');
        this.loadBusinessProcesses();
      }
    });
  }
}
